import moment, { Locale, Moment } from 'moment';
import React, { ComponentPropsWithoutRef, useRef, useState } from 'react';
import ReactSelect from 'react-select';
import styled, { css } from 'styled-components';
import FA from '../../../components/atoms/FontAwesome';
import useClickOutside from '../hooks/useClickOutside';

type Props = {
  value?: Moment | null;
  initialViewDate?: Moment | null;
  onChange?: (date: Moment | null) => void;
  isValidDate?: (currentDate: Moment, selectedDate?: Moment | null) => boolean;
  yearRange?: {
    from: number;
    to: number;
  };
  isDisabled?: boolean;
};

const DateTimeContainer = styled.div`
  position: relative;
  display: flex;
  gap: 12px;
`;

const DateTimeSection = styled.div``;

const Input = styled.div`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.gray[100]};
    border-radius: ${theme.borderRadius[4]};
    height: 40px;
    padding: ${theme.spacing[8]};
    display: flex;
    position: relative;
  `}
`;

const DateInput = styled(Input)<{ isDisabled?: boolean }>`
  width: 152px;
  background: ${({ theme, isDisabled = false }) =>
    isDisabled ? theme.colors.gray[50] : theme.colors.white};
`;

const TimeInput = styled(Input)<{ isDisabled?: boolean }>`
  width: 100px;
  background: ${({ theme, isDisabled = false }) =>
    isDisabled ? theme.colors.gray[50] : theme.colors.white};
`;

const DateTimeIcon = styled(FA)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 16px;
  color: ${(props) => props.theme.colors.gray[200]};
`;

const Slash = styled.span`
  &:after {
    content: '/';
  }
`;

const Colon = styled.span`
  &:after {
    content: ':';
  }
`;

const ValueDisplay = styled.div<{ isPlaceholder?: boolean }>`
  ${({ theme, isPlaceholder = false }) => css`
    display: flex;
    gap: 4px;
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.gray[isPlaceholder ? 200 : 300]};

    ${Slash}, ${Colon} {
      color: ${theme.colors.gray[200]};
    }
  `}
`;

const Calendar = styled.div<{ open?: boolean }>`
  ${({ theme, open = false }) => css`
    display: ${open ? 'block' : 'none'};
    position: absolute;
    padding: ${theme.spacing[24]};
    margin-top: 4px;
    z-index: 99990 !important;
    background: #fff;
    box-shadow: ${theme.shadows.m};
    border: 1px solid ${theme.colors.gray[100]};
    border-radius: ${theme.borderRadius[4]};
  `}
`;

const TimePicker = styled.div<{ open?: boolean }>`
  ${({ theme, open = false }) => css`
    display: ${open ? 'block' : 'none'};
    position: absolute;
    margin-top: 4px;
    z-index: 99990 !important;
    background: #fff;
    box-shadow: ${theme.shadows.m};
    border: 1px solid ${theme.colors.gray[100]};
    border-radius: ${theme.borderRadius[4]};

    max-height: 240px;
    overflow-y: auto;
  `}
`;

const TimeColumnWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
  `}
`;

const TimeColumn = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-width: 52px;
  `}
`;

const ColumnDivider = styled.div`
  ${({ theme }) => css`
    flex: 1;
    border-right: 1px solid ${theme.colors.gray[100]};
  `}
`;

const TimeItem = styled.div<{ selected?: boolean }>`
  ${({ theme, selected = false }) => css`
    display: flex;
    padding: 4px 12px;
    justify-content: center;
    align-items: center;

    background: ${selected ? theme.colors.blue[300] : 'transparent'};
    color: ${selected ? theme.colors.white : theme.colors.gray[300]};
    cursor: pointer;

    :hover {
      background: ${selected ? theme.colors.blue[300] : 'transparent'};
      color: ${selected ? theme.colors.white : theme.colors.gray[300]};
    }
  `}
`;

const DaysTable = styled.table`
  ${({ theme }) => css`
    width: 100%;
    margin: 0;
    color: ${theme.colors.gray[300]};
    font-size: ${theme.typography.fontSize.sm};

    th,
    td {
      text-align: center;
    }

    th {
      font-weight: ${theme.typography.fontWeight.bold};
    }
  `}
`;

const CalendarDay = styled.td<{
  isOld?: boolean;
  isNew?: boolean;
  isActive?: boolean;
  isToday?: boolean;
  isValid?: boolean;
}>`
  ${({
    theme,
    isOld = false,
    isNew = false,
    isActive = false,
    isToday = false,
    isValid = true,
  }) => css`
    width: 32px;
    height: 32px;
    border-radius: ${theme.borderRadius[8]};

    ${(isOld || isNew) &&
    `
      color: ${theme.colors.gray[200]};
    `};

    ${!isValid &&
    `
      color: ${theme.colors.gray[100]};
    `};

    ${isToday &&
    `
      background: ${theme.colors.blue[100]};
    `}

    ${isActive &&
    `
      color: ${theme.colors.white};
      font-weight: ${theme.typography.fontWeight.bold};
      background: ${theme.colors.blue[400]};
    `}

    ${isValid &&
    `
      cursor: pointer;
      ${
        !isActive &&
        `&:hover {
        background: ${theme.colors.blue[100]};
      }
    `
      }
    `}
  `}
`;

const NavigationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 4px 16px;
`;

const NavigationIcon = styled.div`
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray[300]};
`;

const SelectorContainer = styled.div`
  display: flex;
  gap: 4px;
`;

function getDaysOfWeek(locale: Locale) {
  const first = locale.firstDayOfWeek();
  const dow: string[] = [];
  let i = 0;

  locale.weekdaysMin().forEach((day) => {
    dow[(7 + i - first) % 7] = day;
    i += 1;
  });

  return dow;
}

const yearMonthSelectorConfig: ComponentPropsWithoutRef<typeof ReactSelect> = {
  placeholder: '',
  components: {
    IndicatorSeparator: () => null,
  },
  styles: {
    container: (provided) => ({
      ...provided,
      fontSize: 14,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: 0,
      paddingRight: 2,
    }),
    control: (provided) => ({
      ...provided,
      width: 80,
      minHeight: 32,
      lineHeight: 1,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
      paddingLeft: 4,
    }),
  },
  isSearchable: false,
  isMulti: false,
  minMenuHeight: 400,
};

const DateTime = ({
  initialViewDate,
  value,
  isValidDate = () => true,
  yearRange = {
    from: 1900,
    to: 2099,
  },
  onChange,
  isDisabled,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);

  const [viewDate, setViewDate] = useState((initialViewDate ?? moment()).locale('ja-JP'));

  useClickOutside(calendarRef, () => {
    setOpen(false);
  });

  useClickOutside(timePickerRef, () => {
    setTimeOpen(false);
  });

  const renderNavigationHeader = () => {
    return (
      <NavigationHeader>
        <NavigationIcon onClick={() => setViewDate(viewDate.clone().subtract(1, 'month'))}>
          <FA icon="chevron-left" />
        </NavigationIcon>
        <SelectorContainer>
          <ReactSelect
            {...yearMonthSelectorConfig}
            options={[...Array(200)].map((_, i) => ({
              label: `${i + yearRange.from}`,
              value: i + yearRange.from,
            }))}
            value={{
              label: `${viewDate.year()}年`,
              value: viewDate.year(),
            }}
            onChange={(option: { value: number }) =>
              setViewDate(viewDate.clone().year(option.value))
            }
          />
          <ReactSelect
            {...yearMonthSelectorConfig}
            styles={{
              ...yearMonthSelectorConfig.styles,
              control: (provided, props) => ({
                ...yearMonthSelectorConfig.styles?.control?.(provided, props),
                width: 68,
              }),
            }}
            options={[...Array(12)].map((_, i) => ({
              label: `${i + 1}`,
              value: i + 1,
            }))}
            value={{
              label: `${viewDate.month() + 1}月`,
              value: viewDate.month() + 1,
            }}
            onChange={(option: { value: number }) =>
              setViewDate(viewDate.clone().month(option.value - 1))
            }
          />
        </SelectorContainer>
        <NavigationIcon onClick={() => setViewDate(viewDate.clone().add(1, 'month'))}>
          <FA icon="chevron-right" />
        </NavigationIcon>
      </NavigationHeader>
    );
  };

  const renderDayHeaders = () => {
    const locale = viewDate.localeData();
    const dayNames = getDaysOfWeek(locale);

    return (
      <tr>
        {dayNames.map((day) => (
          <th key={day}>{day}</th>
        ))}
      </tr>
    );
  };

  const renderDay = (date: Moment, startOfMonth: Moment, endOfMonth: Moment) => {
    const isOld = date.isBefore(startOfMonth);
    const isNew = date.isAfter(endOfMonth);
    const isToday = date.isSame(moment(), 'day');
    const isActive = !!value && date.isSame(value, 'day');
    const isValid = isValidDate(date, value);
    return (
      <CalendarDay
        isOld={isOld}
        isNew={isNew}
        isToday={isToday}
        isActive={isActive}
        isValid={isValid}
        onClick={() => {
          if (isOld || isNew) {
            setViewDate(date.clone());
          }
          if (isValid) {
            onChange?.(date.clone());
          }
        }}
      >
        {date.date()}
      </CalendarDay>
    );
  };

  const renderDays = () => {
    const startOfMonth = viewDate.clone().startOf('month');
    const endOfMonth = viewDate.clone().endOf('month');

    const startDate = viewDate.clone().subtract(1, 'months').endOf('month').startOf('week');

    return [...Array(6)].map((_, row) => (
      <tr key={row}>
        {[...Array(7)].map((__, col) =>
          renderDay(startDate.clone().add(row * 7 + col, 'd'), startOfMonth, endOfMonth)
        )}
      </tr>
    ));
  };

  return (
    <DateTimeContainer>
      <DateTimeSection>
        <DateInput onClick={() => !isDisabled && setOpen(true)} isDisabled={isDisabled}>
          <ValueDisplay isPlaceholder={!value}>
            {value ? value.format('YYYY') : 'yyyy'}
            <Slash />
            {value ? value.format('MM') : 'mm'}
            <Slash />
            {value ? value.format('DD') : 'dd'}
          </ValueDisplay>
          <DateTimeIcon icon="calendar-alt" />
        </DateInput>
        <Calendar ref={calendarRef} open={open}>
          {renderNavigationHeader()}
          <DaysTable>
            <thead>{renderDayHeaders()}</thead>
            <tbody>{renderDays()}</tbody>
          </DaysTable>
        </Calendar>
      </DateTimeSection>
      <DateTimeSection>
        <TimeInput onClick={() => !isDisabled && setTimeOpen(true)} isDisabled={isDisabled}>
          <ValueDisplay isPlaceholder={!value}>
            {value ? value.format('HH') : 'hh'}
            <Colon />
            {value ? value.format('mm') : 'mm'}
          </ValueDisplay>
          <DateTimeIcon icon="clock" />
        </TimeInput>
        <TimePicker ref={timePickerRef} open={timeOpen}>
          <TimeColumnWrapper>
            <TimeColumn>
              {[...Array(24)].map((_, idx) => (
                <TimeItem
                  key={idx}
                  selected={value?.hour() === idx}
                  onClick={() => onChange?.((value ?? moment().minute(0)).clone().hour(idx))}
                >
                  {idx.toString().padStart(2, '0')}
                </TimeItem>
              ))}
            </TimeColumn>
            <ColumnDivider />
            <TimeColumn>
              {[0, 15, 30, 45].map((minute) => (
                <TimeItem
                  key={minute}
                  selected={value?.minute() === minute}
                  onClick={() => onChange?.((value ?? moment().hour(0)).clone().minute(minute))}
                >
                  {minute.toString().padStart(2, '0')}
                </TimeItem>
              ))}
            </TimeColumn>
          </TimeColumnWrapper>
        </TimePicker>
      </DateTimeSection>
    </DateTimeContainer>
  );
};

export default DateTime;
